import React from 'react';
import { Eye, EyeOff } from 'lucide-react';

// Stock Market Background Component - Dense & Full
const StockBackground = () => (
  <div className="absolute inset-0 overflow-hidden">
    {/* Dark gradient base */}
    <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"></div>
    
    {/* Dense Grid */}
    <div className="absolute inset-0" style={{
      backgroundImage: `
        linear-gradient(rgba(100, 116, 139, 0.1) 1px, transparent 1px),
        linear-gradient(90deg, rgba(100, 116, 139, 0.1) 1px, transparent 1px)
      `,
      backgroundSize: '25px 25px'
    }}></div>
    
    {/* Many Chart Lines */}
    <svg className="absolute inset-0 w-full h-full">
      <defs>
        <linearGradient id="line1" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#64748b" stopOpacity="0.1" />
          <stop offset="50%" stopColor="#64748b" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#64748b" stopOpacity="0.1" />
        </linearGradient>
        <linearGradient id="line2" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#94a3b8" stopOpacity="0.1" />
          <stop offset="50%" stopColor="#94a3b8" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#94a3b8" stopOpacity="0.1" />
        </linearGradient>
        <linearGradient id="fill1" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#64748b" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#64748b" stopOpacity="0" />
        </linearGradient>
      </defs>
      
      {/* Multiple rising charts */}
      <path d="M0,380 Q100,350 200,340 T400,300 T600,280 T800,240 T1000,220 T1200,180 T1400,160 T1600,130 T1800,110 T2000,80" fill="none" stroke="url(#line1)" strokeWidth="2.5" />
      <path d="M0,380 Q100,350 200,340 T400,300 T600,280 T800,240 T1000,220 T1200,180 T1400,160 T1600,130 T1800,110 T2000,80 L2000,600 L0,600 Z" fill="url(#fill1)" />
      
      <path d="M0,420 Q150,400 300,380 T600,350 T900,310 T1200,280 T1500,240 T1800,210 T2000,180" fill="none" stroke="url(#line2)" strokeWidth="2" opacity="0.7" />
      <path d="M0,460 Q200,440 400,420 T800,380 T1200,340 T1600,300 T2000,260" fill="none" stroke="url(#line2)" strokeWidth="1.5" opacity="0.5" />
      <path d="M0,500 Q180,480 360,460 T720,420 T1080,380 T1440,340 T1800,300 T2000,270" fill="none" stroke="url(#line2)" strokeWidth="1" opacity="0.4" />
      <path d="M0,530 Q250,510 500,490 T1000,440 T1500,390 T2000,340" fill="none" stroke="url(#line2)" strokeWidth="0.8" opacity="0.3" />
      <path d="M0,560 Q300,540 600,520 T1200,470 T1800,420 T2000,390" fill="none" stroke="url(#line2)" strokeWidth="0.5" opacity="0.2" />
    </svg>

    {/* Many Up Arrows - Left Side */}
    <div className="absolute top-[5%] left-[3%] text-slate-500/40 text-2xl">▲</div>
    <div className="absolute top-[8%] left-[8%] text-slate-400/30 text-4xl animate-pulse">▲</div>
    <div className="absolute top-[15%] left-[2%] text-slate-500/35 text-3xl">▲</div>
    <div className="absolute top-[18%] left-[12%] text-slate-400/25 text-5xl animate-pulse" style={{animationDelay: '0.3s'}}>▲</div>
    <div className="absolute top-[25%] left-[5%] text-slate-500/30 text-2xl">▲</div>
    <div className="absolute top-[30%] left-[10%] text-slate-400/40 text-3xl animate-pulse" style={{animationDelay: '0.6s'}}>▲</div>
    <div className="absolute top-[35%] left-[3%] text-slate-500/25 text-4xl">▲</div>
    <div className="absolute top-[42%] left-[8%] text-slate-400/35 text-2xl animate-pulse" style={{animationDelay: '0.9s'}}>▲</div>
    <div className="absolute top-[48%] left-[2%] text-slate-500/30 text-5xl">▲</div>
    <div className="absolute top-[55%] left-[12%] text-slate-400/40 text-3xl animate-pulse" style={{animationDelay: '1.2s'}}>▲</div>
    <div className="absolute top-[60%] left-[5%] text-slate-500/25 text-2xl">▲</div>
    <div className="absolute top-[68%] left-[10%] text-slate-400/35 text-4xl animate-pulse" style={{animationDelay: '0.4s'}}>▲</div>
    <div className="absolute top-[75%] left-[3%] text-slate-500/30 text-3xl">▲</div>
    <div className="absolute top-[82%] left-[8%] text-slate-400/40 text-2xl animate-pulse" style={{animationDelay: '0.7s'}}>▲</div>
    
    {/* Many Up Arrows - Right Side */}
    <div className="absolute top-[5%] right-[5%] text-slate-400/35 text-3xl">▲</div>
    <div className="absolute top-[10%] right-[10%] text-slate-500/40 text-4xl animate-pulse" style={{animationDelay: '0.2s'}}>▲</div>
    <div className="absolute top-[15%] right-[3%] text-slate-400/30 text-2xl">▲</div>
    <div className="absolute top-[22%] right-[8%] text-slate-500/35 text-5xl animate-pulse" style={{animationDelay: '0.5s'}}>▲</div>
    <div className="absolute top-[28%] right-[12%] text-slate-400/40 text-3xl">▲</div>
    <div className="absolute top-[35%] right-[5%] text-slate-500/25 text-2xl animate-pulse" style={{animationDelay: '0.8s'}}>▲</div>
    <div className="absolute top-[42%] right-[10%] text-slate-400/35 text-4xl">▲</div>
    <div className="absolute top-[50%] right-[3%] text-slate-500/40 text-3xl animate-pulse" style={{animationDelay: '1.1s'}}>▲</div>
    <div className="absolute top-[58%] right-[8%] text-slate-400/30 text-5xl">▲</div>
    <div className="absolute top-[65%] right-[12%] text-slate-500/35 text-2xl animate-pulse" style={{animationDelay: '0.3s'}}>▲</div>
    <div className="absolute top-[72%] right-[5%] text-slate-400/40 text-4xl">▲</div>
    <div className="absolute top-[80%] right-[10%] text-slate-500/30 text-3xl animate-pulse" style={{animationDelay: '0.6s'}}>▲</div>
    <div className="absolute top-[85%] right-[3%] text-slate-400/35 text-2xl">▲</div>

    {/* Trend Arrows - Multiple */}
    <svg className="absolute top-[12%] left-[18%] w-14 h-14 opacity-25">
      <path d="M4,35 L30,8 M30,8 L30,20 M30,8 L18,8" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </svg>
    <svg className="absolute top-[25%] left-[22%] w-10 h-10 opacity-20">
      <path d="M3,25 L22,5 M22,5 L22,14 M22,5 L13,5" stroke="#64748b" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    </svg>
    <svg className="absolute top-[40%] left-[15%] w-16 h-16 opacity-30">
      <path d="M5,42 L38,8 M38,8 L38,22 M38,8 L24,8" stroke="#94a3b8" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
    </svg>
    <svg className="absolute top-[55%] left-[20%] w-12 h-12 opacity-20">
      <path d="M4,32 L28,6 M28,6 L28,16 M28,6 L18,6" stroke="#64748b" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </svg>
    <svg className="absolute top-[70%] left-[18%] w-10 h-10 opacity-25">
      <path d="M3,26 L24,5 M24,5 L24,14 M24,5 L15,5" stroke="#94a3b8" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    </svg>
    
    <svg className="absolute top-[15%] right-[18%] w-12 h-12 opacity-20">
      <path d="M4,30 L26,6 M26,6 L26,16 M26,6 L16,6" stroke="#64748b" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </svg>
    <svg className="absolute top-[30%] right-[22%] w-16 h-16 opacity-25">
      <path d="M5,40 L36,8 M36,8 L36,20 M36,8 L24,8" stroke="#94a3b8" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
    </svg>
    <svg className="absolute top-[48%] right-[15%] w-10 h-10 opacity-30">
      <path d="M3,25 L22,5 M22,5 L22,13 M22,5 L14,5" stroke="#64748b" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    </svg>
    <svg className="absolute top-[62%] right-[20%] w-14 h-14 opacity-20">
      <path d="M4,36 L32,6 M32,6 L32,18 M32,6 L20,6" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </svg>
    <svg className="absolute top-[78%] right-[18%] w-12 h-12 opacity-25">
      <path d="M4,30 L28,6 M28,6 L28,16 M28,6 L18,6" stroke="#64748b" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </svg>

    {/* Candlestick Groups - Multiple */}
    <div className="absolute top-[20%] left-[28%] flex gap-1 opacity-25">
      <div className="w-1.5 h-10 bg-slate-400 rounded-sm"></div>
      <div className="w-1.5 h-14 bg-slate-400 rounded-sm"></div>
      <div className="w-1.5 h-8 bg-slate-500 rounded-sm mt-3"></div>
      <div className="w-1.5 h-12 bg-slate-400 rounded-sm mt-1"></div>
    </div>
    <div className="absolute top-[45%] left-[25%] flex gap-1 opacity-20">
      <div className="w-2 h-12 bg-slate-400 rounded-sm"></div>
      <div className="w-2 h-8 bg-slate-500 rounded-sm mt-2"></div>
      <div className="w-2 h-16 bg-slate-400 rounded-sm"></div>
      <div className="w-2 h-10 bg-slate-400 rounded-sm mt-3"></div>
      <div className="w-2 h-14 bg-slate-400 rounded-sm mt-1"></div>
    </div>
    <div className="absolute top-[65%] left-[30%] flex gap-1 opacity-25">
      <div className="w-1.5 h-8 bg-slate-400 rounded-sm"></div>
      <div className="w-1.5 h-12 bg-slate-400 rounded-sm"></div>
      <div className="w-1.5 h-6 bg-slate-500 rounded-sm mt-3"></div>
      <div className="w-1.5 h-10 bg-slate-400 rounded-sm mt-1"></div>
    </div>
    
    <div className="absolute top-[18%] right-[28%] flex gap-1 opacity-20">
      <div className="w-1.5 h-12 bg-slate-400 rounded-sm"></div>
      <div className="w-1.5 h-8 bg-slate-500 rounded-sm mt-2"></div>
      <div className="w-1.5 h-14 bg-slate-400 rounded-sm"></div>
      <div className="w-1.5 h-10 bg-slate-400 rounded-sm mt-2"></div>
    </div>
    <div className="absolute top-[42%] right-[25%] flex gap-1 opacity-25">
      <div className="w-2 h-10 bg-slate-400 rounded-sm"></div>
      <div className="w-2 h-14 bg-slate-400 rounded-sm"></div>
      <div className="w-2 h-6 bg-slate-500 rounded-sm mt-4"></div>
      <div className="w-2 h-12 bg-slate-400 rounded-sm mt-1"></div>
      <div className="w-2 h-8 bg-slate-400 rounded-sm mt-3"></div>
    </div>
    <div className="absolute top-[68%] right-[30%] flex gap-1 opacity-20">
      <div className="w-1.5 h-14 bg-slate-400 rounded-sm"></div>
      <div className="w-1.5 h-10 bg-slate-400 rounded-sm mt-2"></div>
      <div className="w-1.5 h-8 bg-slate-500 rounded-sm mt-3"></div>
      <div className="w-1.5 h-12 bg-slate-400 rounded-sm mt-1"></div>
    </div>

    {/* Bottom Candlesticks - Larger */}
    <div className="absolute bottom-[8%] left-[15%] flex gap-1.5 opacity-35">
      <div className="w-3 h-16 bg-slate-400 rounded-sm"></div>
      <div className="w-3 h-10 bg-slate-500 rounded-sm mt-6"></div>
      <div className="w-3 h-20 bg-slate-400 rounded-sm"></div>
      <div className="w-3 h-8 bg-slate-500 rounded-sm mt-6"></div>
      <div className="w-3 h-14 bg-slate-400 rounded-sm mt-3"></div>
      <div className="w-3 h-18 bg-slate-400 rounded-sm mt-1"></div>
      <div className="w-3 h-12 bg-slate-400 rounded-sm mt-4"></div>
    </div>
    <div className="absolute bottom-[10%] right-[15%] flex gap-1.5 opacity-30">
      <div className="w-2.5 h-14 bg-slate-400 rounded-sm"></div>
      <div className="w-2.5 h-18 bg-slate-400 rounded-sm"></div>
      <div className="w-2.5 h-10 bg-slate-500 rounded-sm mt-4"></div>
      <div className="w-2.5 h-16 bg-slate-400 rounded-sm mt-1"></div>
      <div className="w-2.5 h-12 bg-slate-400 rounded-sm mt-3"></div>
      <div className="w-2.5 h-20 bg-slate-400 rounded-sm"></div>
    </div>

    {/* Horizontal price levels */}
    <div className="absolute top-[20%] left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-500/25 to-transparent"></div>
    <div className="absolute top-[35%] left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-500/20 to-transparent"></div>
    <div className="absolute top-[50%] left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-500/15 to-transparent"></div>
    <div className="absolute top-[65%] left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-500/20 to-transparent"></div>
    <div className="absolute top-[80%] left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-500/25 to-transparent"></div>

    {/* Volume bars - Dense */}
    <div className="absolute bottom-0 left-0 right-0 h-14 flex items-end gap-px px-4 opacity-15">
      {[35,55,25,70,45,60,30,80,50,40,65,35,75,45,55,65,40,70,35,60,45,75,30,85,55,40,70,50,60,80,35,55,45,70,40,65,50,75,35,60,45,80,55,70,40,65,50,75,35,55].map((h, i) => (
        <div key={i} className="flex-1 bg-slate-400 rounded-t-sm" style={{height: `${h}%`}}></div>
      ))}
    </div>

    {/* Extra decorative dots */}
    <div className="absolute top-[10%] left-[35%] w-1 h-1 bg-slate-500/40 rounded-full"></div>
    <div className="absolute top-[25%] left-[40%] w-1.5 h-1.5 bg-slate-400/30 rounded-full"></div>
    <div className="absolute top-[40%] left-[32%] w-1 h-1 bg-slate-500/35 rounded-full"></div>
    <div className="absolute top-[55%] left-[38%] w-2 h-2 bg-slate-400/25 rounded-full"></div>
    <div className="absolute top-[70%] left-[35%] w-1 h-1 bg-slate-500/40 rounded-full"></div>
    <div className="absolute top-[15%] right-[35%] w-1.5 h-1.5 bg-slate-400/35 rounded-full"></div>
    <div className="absolute top-[30%] right-[40%] w-1 h-1 bg-slate-500/30 rounded-full"></div>
    <div className="absolute top-[50%] right-[32%] w-2 h-2 bg-slate-400/40 rounded-full"></div>
    <div className="absolute top-[65%] right-[38%] w-1 h-1 bg-slate-500/25 rounded-full"></div>
    <div className="absolute top-[80%] right-[35%] w-1.5 h-1.5 bg-slate-400/35 rounded-full"></div>
  </div>
);

// Design 1: Görsel Arka Planlı Profesyonel
export const Design1 = ({ formData, setFormData, handleSubmit, loading, error, isLogin, setIsLogin, showPassword, setShowPassword, broker }) => (
  <div
    className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
    style={{
      backgroundImage: "url('/images/login-bg.png')",
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed'
    }}
  >
    {/* Karanlık overlay - form okunabilirliği için */}
    <div className="absolute inset-0 bg-slate-900/50"></div>

    <div className="bg-white/95 backdrop-blur-md w-full max-w-md p-10 shadow-2xl relative z-10 rounded-xl border border-white/20">
      <div className="mb-8">
        {broker?.logo_url && <img src={broker.logo_url} alt={broker.name} className="h-12 mb-6 mx-auto" />}
        <h1 className="text-3xl font-semibold text-slate-900 mb-2 text-center">{broker?.name || 'Yatırım Platformu'}</h1>
        <p className="text-slate-600 text-center">Hesabınıza Giriş Yapın</p>
      </div>
      <div className="flex gap-1 mb-8 border-b border-slate-200">
        <button onClick={() => setIsLogin(true)} className={`flex-1 py-3 font-medium transition ${isLogin ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-400'}`}>Giriş</button>
        <button onClick={() => setIsLogin(false)} className={`flex-1 py-3 font-medium transition ${!isLogin ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-400'}`}>Kayıt</button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        {!isLogin && <input type="text" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none" placeholder="Ad Soyad" required />}
        <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none" placeholder="Email" required />
        <input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none" placeholder="Şifre" required />
        {!isLogin && (
          <>
            <input type="password" value={formData.password_confirm} onChange={(e) => setFormData({...formData, password_confirm: e.target.value})} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none" placeholder="Şifre Tekrar" required />
            <input type="text" value={formData.tc_no} onChange={(e) => setFormData({...formData, tc_no: e.target.value})} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none" placeholder="TC Kimlik No" required />
            <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none" placeholder="Telefon" required />
          </>
        )}
        {error && <div className="text-red-700 text-sm bg-red-50 p-3 rounded-lg border border-red-200">{error}</div>}
        <button type="submit" disabled={loading} className="w-full py-4 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition">
          {loading ? 'Yükleniyor...' : isLogin ? 'Giriş Yap' : 'Kayıt Ol'}
        </button>
      </form>
    </div>
  </div>
);

// Design 2: Corporate Split Dark Blue
export const Design2 = ({ formData, setFormData, handleSubmit, loading, error, isLogin, setIsLogin, showPassword, setShowPassword, broker }) => (
  <div className="min-h-screen flex flex-col lg:flex-row">
    <div className="lg:w-1/2 bg-slate-800 flex items-center justify-center p-12 text-white">
      <div className="max-w-md">
        {broker?.logo_url && <img src={broker.logo_url} alt={broker.name} className="h-16 mb-8 mx-auto brightness-0 invert" />}
        <h1 className="text-4xl font-bold mb-4 text-center">{broker?.name || 'Yatırım Platformu'}</h1>
        <p className="text-xl text-slate-300 text-center">Güvenli ve profesyonel yatırım çözümleri</p>
      </div>
    </div>
    <div className="lg:w-1/2 flex items-center justify-center p-8 bg-white">
      <div className="w-full max-w-md">
        <div className="flex gap-1 mb-8 border-b border-slate-200">
          <button onClick={() => setIsLogin(true)} className={`px-8 py-3 font-semibold ${isLogin ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-400'}`}>Giriş</button>
          <button onClick={() => setIsLogin(false)} className={`px-8 py-3 font-semibold ${!isLogin ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-400'}`}>Kayıt</button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          {!isLogin && <input type="text" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:border-slate-900 outline-none" placeholder="Ad Soyad" required />}
          <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:border-slate-900 outline-none" placeholder="Email" required />
          <input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:border-slate-900 outline-none" placeholder="Şifre" required />
          {!isLogin && (
            <>
              <input type="password" value={formData.password_confirm} onChange={(e) => setFormData({...formData, password_confirm: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:border-slate-900 outline-none" placeholder="Şifre Tekrar" required />
              <input type="text" value={formData.tc_no} onChange={(e) => setFormData({...formData, tc_no: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:border-slate-900 outline-none" placeholder="TC Kimlik No" required />
              <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:border-slate-900 outline-none" placeholder="Telefon" required />
            </>
          )}
          {error && <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200">{error}</div>}
          <button type="submit" className="w-full py-4 bg-slate-900 text-white font-semibold hover:bg-slate-800 transition">{loading ? 'Yükleniyor...' : isLogin ? 'Giriş Yap' : 'Hesap Oluştur'}</button>
        </form>
      </div>
    </div>
  </div>
);

// Design 3: Clean White Professional
export const Design3 = ({ formData, setFormData, handleSubmit, loading, error, isLogin, setIsLogin, broker }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
    <div className="bg-white shadow-xl w-full max-w-md p-10">
      <div className="mb-8">
        {broker?.logo_url && <img src={broker.logo_url} alt={broker.name} className="h-12 mb-6 mx-auto" />}
        <h2 className="text-2xl font-semibold text-slate-900 text-center">{isLogin ? 'Giriş Yap' : 'Hesap Oluştur'}</h2>
        <p className="text-slate-500 mt-1 text-sm text-center">Yatırım hesabınıza erişin</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        {!isLogin && <input type="text" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} className="w-full px-4 py-3 border-b-2 border-slate-200 focus:border-slate-900 outline-none bg-transparent" placeholder="Ad Soyad" required />}
        <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 border-b-2 border-slate-200 focus:border-slate-900 outline-none bg-transparent" placeholder="Email" required />
        <input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} className="w-full px-4 py-3 border-b-2 border-slate-200 focus:border-slate-900 outline-none bg-transparent" placeholder="Şifre" required />
        {!isLogin && (
          <>
            <input type="password" value={formData.password_confirm} onChange={(e) => setFormData({...formData, password_confirm: e.target.value})} className="w-full px-4 py-3 border-b-2 border-slate-200 focus:border-slate-900 outline-none bg-transparent" placeholder="Şifre Tekrar" required />
            <input type="text" value={formData.tc_no} onChange={(e) => setFormData({...formData, tc_no: e.target.value})} className="w-full px-4 py-3 border-b-2 border-slate-200 focus:border-slate-900 outline-none bg-transparent" placeholder="TC Kimlik No" required />
            <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 border-b-2 border-slate-200 focus:border-slate-900 outline-none bg-transparent" placeholder="Telefon" required />
          </>
        )}
        {error && <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200">{error}</div>}
        <button type="submit" className="w-full py-3 bg-slate-900 text-white font-semibold hover:bg-slate-800 transition mt-8">{loading ? 'Yükleniyor...' : isLogin ? 'Giriş Yap' : 'Kayıt Ol'}</button>
        <button type="button" onClick={() => setIsLogin(!isLogin)} className="w-full text-sm text-slate-600 hover:text-slate-900">{isLogin ? 'Hesap oluşturun' : 'Zaten hesabınız var mı?'}</button>
      </form>
    </div>
  </div>
);

// Design 4: Executive Dark Premium
export const Design4 = ({ formData, setFormData, handleSubmit, loading, error, isLogin, setIsLogin, broker }) => (
  <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
    <div className="bg-zinc-900 border border-zinc-800 w-full max-w-md p-10">
      <div className="mb-8">
        {broker?.logo_url && <img src={broker.logo_url} alt={broker.name} className="h-12 mb-6 mx-auto brightness-0 invert" />}
        <h1 className="text-2xl font-semibold text-white mb-1 text-center">{broker?.name || 'Yatırım Platformu'}</h1>
        <p className="text-zinc-400 text-sm text-center">Güvenli giriş</p>
      </div>
      <div className="flex gap-1 mb-8 border-b border-zinc-800">
        <button onClick={() => setIsLogin(true)} className={`flex-1 py-3 font-medium ${isLogin ? 'text-white border-b-2 border-white' : 'text-zinc-500'}`}>Giriş</button>
        <button onClick={() => setIsLogin(false)} className={`flex-1 py-3 font-medium ${!isLogin ? 'text-white border-b-2 border-white' : 'text-zinc-500'}`}>Kayıt</button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        {!isLogin && <input type="text" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-zinc-500 outline-none" placeholder="Ad Soyad" required />}
        <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-zinc-500 outline-none" placeholder="Email" required />
        <input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-zinc-500 outline-none" placeholder="Şifre" required />
        {!isLogin && (
          <>
            <input type="password" value={formData.password_confirm} onChange={(e) => setFormData({...formData, password_confirm: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-zinc-500 outline-none" placeholder="Şifre Tekrar" required />
            <input type="text" value={formData.tc_no} onChange={(e) => setFormData({...formData, tc_no: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-zinc-500 outline-none" placeholder="TC Kimlik No" required />
            <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-zinc-500 outline-none" placeholder="Telefon" required />
          </>
        )}
        {error && <div className="text-red-400 text-sm bg-red-950 bg-opacity-50 p-3 border border-red-900">{error}</div>}
        <button type="submit" className="w-full py-4 bg-white text-zinc-900 font-semibold hover:bg-zinc-100 transition">{loading ? 'Yükleniyor...' : isLogin ? 'Giriş Yap' : 'Hesap Oluştur'}</button>
      </form>
    </div>
  </div>
);

// Design 5: Minimal Centered Professional
export const Design5 = ({ formData, setFormData, handleSubmit, loading, error, isLogin, setIsLogin, broker }) => (
  <div className="min-h-screen bg-white flex items-center justify-center p-4">
    <div className="w-full max-w-sm">
      <div className="text-center mb-10">
        {broker?.logo_url && <img src={broker.logo_url} alt={broker.name} className="h-14 mx-auto mb-8" />}
        <h1 className="text-2xl font-semibold text-slate-900 mb-2">{broker?.name || 'Yatırım Platformu'}</h1>
        <p className="text-slate-500 text-sm">{isLogin ? 'Hesabınıza giriş yapın' : 'Yeni hesap oluşturun'}</p>
      </div>
      <div className="flex gap-2 mb-8">
        <button onClick={() => setIsLogin(true)} className={`flex-1 py-2.5 text-sm font-medium ${isLogin ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>Giriş</button>
        <button onClick={() => setIsLogin(false)} className={`flex-1 py-2.5 text-sm font-medium ${!isLogin ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>Kayıt</button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {!isLogin && <input type="text" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} className="w-full px-4 py-3 border border-slate-200 focus:border-slate-900 outline-none text-sm" placeholder="Ad Soyad" required />}
        <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 border border-slate-200 focus:border-slate-900 outline-none text-sm" placeholder="Email" required />
        <input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} className="w-full px-4 py-3 border border-slate-200 focus:border-slate-900 outline-none text-sm" placeholder="Şifre" required />
        {!isLogin && (
          <>
            <input type="password" value={formData.password_confirm} onChange={(e) => setFormData({...formData, password_confirm: e.target.value})} className="w-full px-4 py-3 border border-slate-200 focus:border-slate-900 outline-none text-sm" placeholder="Şifre Tekrar" required />
            <input type="text" value={formData.tc_no} onChange={(e) => setFormData({...formData, tc_no: e.target.value})} className="w-full px-4 py-3 border border-slate-200 focus:border-slate-900 outline-none text-sm" placeholder="TC Kimlik No" required />
            <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 border border-slate-200 focus:border-slate-900 outline-none text-sm" placeholder="Telefon" required />
          </>
        )}
        {error && <div className="text-red-700 text-xs bg-red-50 p-3 border border-red-200">{error}</div>}
        <button type="submit" className="w-full py-3.5 bg-slate-900 text-white font-medium hover:bg-slate-800 transition text-sm">{loading ? 'Yükleniyor...' : isLogin ? 'Giriş Yap' : 'Kayıt Ol'}</button>
      </form>
    </div>
  </div>
);

// 6-50 için export map
export const designComponents = {
  1: Design1,
  2: Design2,
  3: Design3,
  4: Design4,
  5: Design5
};

// 6-50 arası için fallback (şimdilik design 1-5 arası random)
for (let i = 6; i <= 50; i++) {
  designComponents[i] = designComponents[(i % 5) + 1];
}
